import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    Users, Search, Loader2, Plus, Eye, Mail, Phone, Globe, Building2,
    ArrowRight, MessageSquare, Calendar, DollarSign, GripVertical, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { ProposalGeneratorDialog } from '../../components/admin/ProposalGeneratorDialog';

const statusConfig = {
    new: { label: 'Nuevo', color: 'bg-sky-500/10 text-sky-500 border-sky-500/20' },
    contacted: { label: 'Contactado', color: 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20' },
    qualified: { label: 'Calificado', color: 'bg-amber-500/10 text-amber-500 border-amber-500/20' },
    proposal: { label: 'Propuesta', color: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
    negotiation: { label: 'Negociación', color: 'bg-violet-500/10 text-violet-500 border-violet-500/20' },
    won: { label: 'Ganado', color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' },
    lost: { label: 'Perdido', color: 'bg-red-500/10 text-red-500 border-red-500/20' },
};

const pipelineColumns = ['new', 'contacted', 'qualified', 'proposal', 'negotiation'];

const emptyLead = { company_name: '', contact_name: '', email: '', phone: '', country: '', fleet_size: '', message: '', source: 'manual', interested_plan: '', estimated_value: '' };

export default function AdminLeadsPage() {
    const { apiCall } = useAdminAuth();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('pipeline');
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showCreate, setShowCreate] = useState(false);
    const [showDetail, setShowDetail] = useState(null);
    const [form, setForm] = useState(emptyLead);
    const [submitting, setSubmitting] = useState(false);
    const [activityText, setActivityText] = useState('');
    const [activityType, setActivityType] = useState('note');
    const [proposalTarget, setProposalTarget] = useState(null);

    const fetchLeads = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterStatus !== 'all') params.append('status', filterStatus);
            const res = await apiCall('get', `/admin/leads?${params.toString()}`);
            setLeads(res.data);
        } catch (e) {
            console.error(e);
            toast.error('No se pudieron cargar los leads');
        } finally {
            setLoading(false);
        }
    }, [apiCall, filterStatus]);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    const handleCreate = async () => {
        setSubmitting(true);
        try {
            const payload = { ...form, fleet_size: form.fleet_size ? parseInt(form.fleet_size) : null, estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : 0 };
            await apiCall('post', '/admin/leads', payload);
            toast.success('Lead creado exitosamente');
            setShowCreate(false);
            setForm(emptyLead);
            fetchLeads();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al crear lead');
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (leadId, newStatus) => {
        try {
            await apiCall('put', `/admin/leads/${leadId}`, { status: newStatus });
            toast.success(`Estado actualizado a ${statusConfig[newStatus]?.label}`);
            fetchLeads();
            if (showDetail?.id === leadId) {
                setShowDetail(prev => ({ ...prev, status: newStatus }));
            }
        } catch (e) {
            toast.error('Error al cambiar estado');
        }
    };

    const handleAddActivity = async () => {
        if (!activityText.trim() || !showDetail) return;
        try {
            await apiCall('post', `/admin/leads/${showDetail.id}/activity`, { type: activityType, description: activityText });
            toast.success('Actividad registrada');
            setActivityText('');
            // Refresh lead detail
            const res = await apiCall('get', `/admin/leads/${showDetail.id}`);
            setShowDetail(res.data);
        } catch (e) {
            toast.error('Error al registrar actividad');
        }
    };

    const filteredLeads = leads.filter(l => {
        if (search) {
            const s = search.toLowerCase();
            return l.company_name.toLowerCase().includes(s) || l.contact_name.toLowerCase().includes(s) || l.email.toLowerCase().includes(s);
        }
        return true;
    });

    const getLeadsByStatus = (status) => filteredLeads.filter(l => l.status === status);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="admin-leads-title">
                        Leads & CRM
                    </h1>
                    <p className="text-muted-foreground mt-1">Pipeline de ventas y gestión de prospectos</p>
                </div>
                <div className="flex items-center gap-2">
                    <Tabs value={view} onValueChange={setView}>
                        <TabsList>
                            <TabsTrigger value="pipeline" data-testid="leads-view-pipeline">Pipeline</TabsTrigger>
                            <TabsTrigger value="table" data-testid="leads-view-table">Tabla</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button onClick={() => setShowCreate(true)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="create-lead-btn">
                        <Plus className="w-4 h-4 mr-2" /> Nuevo Lead
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar por empresa, contacto, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 input-aviation" data-testid="leads-search" />
                </div>
                {view === 'table' && (
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[180px]" data-testid="leads-filter-status">
                            <SelectValue placeholder="Estado" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {Object.entries(statusConfig).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                )}
            </div>

            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>
            ) : (
                <>
                    {/* Pipeline View */}
                    {view === 'pipeline' && (
                        <div className="flex gap-4 overflow-x-auto pb-4" data-testid="leads-pipeline">
                            {pipelineColumns.map((col) => {
                                const colLeads = getLeadsByStatus(col);
                                const cfg = statusConfig[col];
                                return (
                                    <div key={col} className="flex-shrink-0 w-72" data-testid={`pipeline-col-${col}`}>
                                        <div className="flex items-center justify-between mb-3 px-1">
                                            <div className="flex items-center gap-2">
                                                <Badge className={cfg.color} variant="outline">{cfg.label}</Badge>
                                                <span className="text-xs text-muted-foreground font-mono">{colLeads.length}</span>
                                            </div>
                                        </div>
                                        <div className="space-y-2 min-h-[200px] p-2 rounded-lg bg-muted/20 border border-border/30">
                                            {colLeads.map((lead) => (
                                                <Card
                                                    key={lead.id}
                                                    className="card-aviation cursor-pointer hover:border-amber-500/30 transition-all"
                                                    onClick={() => setShowDetail(lead)}
                                                    data-testid={`lead-card-${lead.id}`}
                                                >
                                                    <CardContent className="p-3">
                                                        <div className="flex items-start justify-between">
                                                            <div className="min-w-0 flex-1">
                                                                <p className="font-medium text-sm truncate">{lead.company_name}</p>
                                                                <p className="text-xs text-muted-foreground truncate">{lead.contact_name}</p>
                                                            </div>
                                                            <GripVertical className="w-4 h-4 text-muted-foreground/30 flex-shrink-0" />
                                                        </div>
                                                        <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                                                            <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{lead.country}</span>
                                                            {lead.fleet_size && <span>{lead.fleet_size} aeronaves</span>}
                                                        </div>
                                                        {lead.estimated_value > 0 && (
                                                            <div className="mt-2 flex items-center gap-1 text-xs font-mono text-emerald-500">
                                                                <DollarSign className="w-3 h-3" />{lead.estimated_value.toLocaleString()}
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                            {colLeads.length === 0 && (
                                                <div className="text-center py-8 text-xs text-muted-foreground">Sin leads</div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Table View */}
                    {view === 'table' && (
                        <Card className="card-aviation overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="table-aviation" data-testid="leads-table">
                                    <thead>
                                        <tr>
                                            <th>Empresa</th>
                                            <th>Contacto</th>
                                            <th>Email</th>
                                            <th>País</th>
                                            <th>Flota</th>
                                            <th>Estado</th>
                                            <th>Valor Est.</th>
                                            <th>Fuente</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredLeads.map((lead) => (
                                            <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                                                <td className="font-medium">{lead.company_name}</td>
                                                <td className="text-sm">{lead.contact_name}</td>
                                                <td className="text-xs font-mono">{lead.email}</td>
                                                <td className="text-sm">{lead.country}</td>
                                                <td className="text-sm font-mono text-center">{lead.fleet_size || '-'}</td>
                                                <td><Badge className={statusConfig[lead.status]?.color} variant="outline">{statusConfig[lead.status]?.label}</Badge></td>
                                                <td className="font-mono text-sm">{lead.estimated_value > 0 ? `$${lead.estimated_value.toLocaleString()}` : '-'}</td>
                                                <td className="text-xs">{lead.source}</td>
                                                <td>
                                                    <Button variant="ghost" size="sm" onClick={() => setShowDetail(lead)} data-testid={`view-lead-${lead.id}`}>
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredLeads.length === 0 && (
                                            <tr><td colSpan={9} className="text-center py-12 text-muted-foreground">
                                                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                                <p>No se encontraron leads</p>
                                            </td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    )}
                </>
            )}

            {/* Create Lead Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight">Nuevo Lead</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Empresa</Label>
                                <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Aerolínea XYZ" className="input-aviation" data-testid="create-lead-company" />
                            </div>
                            <div className="space-y-2">
                                <Label>Contacto</Label>
                                <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Juan Pérez" className="input-aviation" data-testid="create-lead-contact" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="ops@airline.com" className="input-aviation" data-testid="create-lead-email" />
                            </div>
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+57 300 123" className="input-aviation" data-testid="create-lead-phone" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>País</Label>
                                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="Colombia" className="input-aviation" data-testid="create-lead-country" />
                            </div>
                            <div className="space-y-2">
                                <Label>Tamaño de Flota</Label>
                                <Input type="number" value={form.fleet_size} onChange={(e) => setForm({ ...form, fleet_size: e.target.value })} placeholder="5" className="input-aviation" data-testid="create-lead-fleet" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Plan de Interés</Label>
                                <Select value={form.interested_plan} onValueChange={(v) => setForm({ ...form, interested_plan: v })}>
                                    <SelectTrigger data-testid="create-lead-plan"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="free">Free</SelectItem>
                                        <SelectItem value="basic">Basic</SelectItem>
                                        <SelectItem value="pro">Pro</SelectItem>
                                        <SelectItem value="enterprise">Enterprise</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Valor Estimado ($)</Label>
                                <Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} placeholder="9600" className="input-aviation font-mono" data-testid="create-lead-value" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Mensaje</Label>
                            <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Notas adicionales..." className="w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary min-h-[60px] resize-none" data-testid="create-lead-message" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
                        <Button onClick={handleCreate} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider text-xs" data-testid="submit-create-lead">
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Crear Lead
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Lead Detail Dialog */}
            <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
                    {showDetail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-['Barlow_Condensed'] text-2xl uppercase tracking-tight flex items-center gap-2">
                                    <Building2 className="w-6 h-6 text-amber-500" />
                                    {showDetail.company_name}
                                </DialogTitle>
                                <div className="flex justify-end -mt-1">
                                    <Button size="sm" variant="outline" onClick={() => setProposalTarget(showDetail)} data-testid={`generate-proposal-lead-${showDetail.id}`}>
                                        <FileText className="w-3.5 h-3.5 mr-1.5" /> Generar propuesta
                                    </Button>
                                </div>
                            </DialogHeader>

                            <div className="space-y-6">
                                {/* Contact Info */}
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" />{showDetail.contact_name}</div>
                                    <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-muted-foreground" />{showDetail.email}</div>
                                    <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground" />{showDetail.phone || '-'}</div>
                                    <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-muted-foreground" />{showDetail.country}</div>
                                </div>

                                {/* Status Pipeline */}
                                <div>
                                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Estado del Pipeline</p>
                                    <div className="flex items-center gap-1 flex-wrap">
                                        {Object.entries(statusConfig).map(([key, cfg]) => (
                                            <Button
                                                key={key}
                                                size="sm"
                                                variant={showDetail.status === key ? 'default' : 'outline'}
                                                className={`text-xs ${showDetail.status === key ? cfg.color.replace('/10', '/20') : 'opacity-60'}`}
                                                onClick={() => handleStatusChange(showDetail.id, key)}
                                                data-testid={`lead-status-${key}`}
                                            >
                                                {cfg.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                {/* Details */}
                                <div className="grid grid-cols-3 gap-4">
                                    <Card className="bg-muted/30"><CardContent className="p-3 text-center">
                                        <p className="text-xs text-muted-foreground">Flota</p>
                                        <p className="font-mono font-bold text-lg">{showDetail.fleet_size || '-'}</p>
                                    </CardContent></Card>
                                    <Card className="bg-muted/30"><CardContent className="p-3 text-center">
                                        <p className="text-xs text-muted-foreground">Plan Interés</p>
                                        <p className="font-bold text-sm">{showDetail.interested_plan || '-'}</p>
                                    </CardContent></Card>
                                    <Card className="bg-muted/30"><CardContent className="p-3 text-center">
                                        <p className="text-xs text-muted-foreground">Valor Est.</p>
                                        <p className="font-mono font-bold text-lg text-emerald-500">${(showDetail.estimated_value || 0).toLocaleString()}</p>
                                    </CardContent></Card>
                                </div>

                                {showDetail.message && (
                                    <div className="p-3 rounded-lg bg-muted/30 text-sm">
                                        <p className="text-xs font-bold uppercase text-muted-foreground mb-1">Mensaje</p>
                                        {showDetail.message}
                                    </div>
                                )}

                                {/* Add Activity */}
                                <div className="border-t border-border/50 pt-4">
                                    <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-3">Registrar Actividad</p>
                                    <div className="flex gap-2">
                                        <Select value={activityType} onValueChange={setActivityType}>
                                            <SelectTrigger className="w-[130px]">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="note">Nota</SelectItem>
                                                <SelectItem value="call">Llamada</SelectItem>
                                                <SelectItem value="email">Email</SelectItem>
                                                <SelectItem value="meeting">Reunión</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Input value={activityText} onChange={(e) => setActivityText(e.target.value)} placeholder="Descripción..." className="flex-1 input-aviation" data-testid="lead-activity-text" onKeyDown={(e) => e.key === 'Enter' && handleAddActivity()} />
                                        <Button onClick={handleAddActivity} size="sm" className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="lead-add-activity">
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Activity History */}
                                {showDetail.activities?.length > 0 && (
                                    <div>
                                        <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">Historial</p>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {showDetail.activities.map((act, i) => (
                                                <div key={i} className="flex items-start gap-3 py-2 border-b border-border/20 last:border-0 text-sm">
                                                    <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant="outline" className="text-xs">{act.type}</Badge>
                                                            <span className="text-xs text-muted-foreground">{act.created_by_name}</span>
                                                        </div>
                                                        <p className="mt-0.5">{act.description}</p>
                                                        <p className="text-xs text-muted-foreground mt-0.5">{new Date(act.created_at).toLocaleString('es-ES')}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Convert to Tenant - show only if not won/lost */}
                                {showDetail.status !== 'won' && showDetail.status !== 'lost' && !showDetail.converted_tenant_id && (
                                    <div className="border-t border-border/50 pt-4">
                                        <Button
                                            variant="outline"
                                            className="w-full border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10"
                                            onClick={() => {
                                                toast.info('Para convertir este lead, ve a Aerolíneas y crea un nuevo tenant con estos datos.');
                                            }}
                                            data-testid="lead-convert-btn"
                                        >
                                            <ArrowRight className="w-4 h-4 mr-2" /> Convertir a Aerolínea
                                        </Button>
                                    </div>
                                )}

                                {showDetail.converted_tenant_id && (
                                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-500 text-center">
                                        Lead convertido a tenant
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <ProposalGeneratorDialog
                open={!!proposalTarget}
                onOpenChange={(v) => !v && setProposalTarget(null)}
                target={proposalTarget}
                targetType="lead"
                apiCall={apiCall}
            />
        </div>
    );
}
